import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RestrictionSchemeType } from '@/data/models/territory';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import ContextErrorAlert from '../../common/ErrorAlert/ContextErrorAlert';
import DismissibleErrorAlert from '../../common/ErrorAlert/DismissibleErrorAlert';
import {
  releasesGridUrl,
  submissionsGridUrl,
} from '../../common/Grid/GridUtilities';
import {
  getActiveChangeRequestUrl,
  getAlbumStateBadgeClass,
} from '../../common/ProductUtilities';
import LabelInfoModal from '../LabelInfoModal/LabelInfoModal';
import TrackEditView from '../ProductTabs/TrackEditView/TrackEditView';
import RestrictionsModal from '../Restrictions/RestrictionsModal';
import ActionDropdownMenu from './ActionDropdownMenu/ActionDropdownMenu';
import ProductMetadata from './ProductMetadata/ProductMetadata';
import './ProductView.scss';
import RejectionBanner from './RejectionBanner/RejectionBanner';
import StateChangeOperations from './StateChangeOperations/StateChangeOperations';
import ValidationErrors from './ValidationErrors/ValidationErrors';
import { FormatType } from '@/data/models/formatType';
import { AlbumState } from '@/data/models/albumState';
import PricingModal from '../MainContainer/Pricing/PricingModal';
import { ProductViewModes } from '@/utils/productStateUtils';
import ProductMetadataTable from '@/components/partials/ProductView/ProductMetadata/ProductMetadataTable';
import ProductDetailsEdit from '@/components/partials/ProductDetailsEdit/ProductDetailsEdit';
import ProductTabs from '@/components/partials/ProductTabs/ProductTabs';
import { ProductViewHook } from '@/types/product-view-model';
import Spinner, { SpinnerSize } from '@/components/common/Spinner/Spinner';
import Uploader from './CoverArt/Uploader';
import { AssetType } from '@/constants/AssetType';
import { translatePhysicalProductType } from '@/helpers/translateProductType';
import ErrorAlert from '../../common/ErrorAlert/ErrorAlert';

interface ProductViewTemplateProps {
  productViewMode: ProductViewModes;
  viewHook: ProductViewHook;
}

const ProductViewTemplate = ({
  productViewMode,
  viewHook,
}: ProductViewTemplateProps) => {
  const { t } = useTranslation();
  const {
    album,
    labelInfo,
    parentLabelInfo,
    restrictions,
    isLoading,
    errorFlag,
    errorBanner,
    errors,
    errorMessages,
    warningMessages,
    currentStateHistory,
    readonly,
    disabled,
    project,
    track,
    multipleTracksEdit,
    showEditProductModal,
    showEditTrackModal,
    showEditMultipleTracksModal,
    showRestrictionsModal,
    showLabelInfoModal,
    showParentLabelInfoModal,
    showPricingModal,
    toggleEditProductModal,
    toggleEditTrackModal,
    toggleEditMultipleTracksModal,
    toggleRestrictionsModal,
    toggleLabelInfoModal,
    toggleParentLabelInfoModal,
    ReloadTracks,
    UpdateProductTracks,
    updateProductRestrictions,
    updateProductPricing,
    updateProductCoverArt,
    loadPreviousTrack,
    loadNextTrack,
    onProductUpdated,
    showValidationErrors,
    setErrorBanner,
    setErrors,
    refetchProject,
    usesSides,
    togglePricingModal,
    removeProject,
    showRemoveFromProjectConfirmationModal,
    setShowRemoveFromProjectConfirmationModal,
    changeRequest,
    currentChanges,
  } = viewHook;

  const [apiError, setApiError] = useState<string | null>(null);
  const [trackSelectionResetSignal, setTrackSelectionResetSignal] = useState(0);
  const handleMultipleTrackEditSaved = () => {
    setTrackSelectionResetSignal((signal) => signal + 1);
  };
  const { DetailsTable, EditMode, Tabs } = useMemo(() => {
    return {
      DetailsTable: ProductMetadataTable,
      EditMode: ProductDetailsEdit,
      Tabs: ProductTabs,
    };
  }, [album]);

  // Helper to determine if we should show validation errors in the modal
  const shouldShowValidationErrors = showValidationErrors();

  return (
    <div className="app-body">
      <ConfirmationModal
        header={t('product.productEditView.confirmRemoveFromProjectHeader')}
        message={t('product.productEditView.confirmRemoveFromProjectMessage', {
          projectName: project?.name,
        })}
        handleConfirm={removeProject}
        showModal={showRemoveFromProjectConfirmationModal}
        setShowModal={setShowRemoveFromProjectConfirmationModal}
      />
      <div className="editProductView">
        {album && !isLoading ? (
          <>
            <div>
              <div className="view-title-container">
                <div className="editProductView-header">
                  <span>
                    {album.albumState === AlbumState.Released ? (
                      <Link to={releasesGridUrl}>
                        {t('product.productList.productList')}
                      </Link>
                    ) : (
                      <Link to={submissionsGridUrl}>
                        {t('product.productList.physicalSubmissions')}
                      </Link>
                    )}
                  </span>
                  <span>&gt;</span>
                  <span>{album.title}</span>
                </div>
                <div className="editProductView-badges">
                  <div className="badge format-badge badge-default">
                    {translatePhysicalProductType(album?.format?.name ?? null)}
                  </div>
                  {productViewMode !== ProductViewModes.ChangeRequest &&
                  productViewMode !== ProductViewModes.ChangeRequestReadOnly ? (
                    <div
                      className={`badge state-badge ${getAlbumStateBadgeClass(album?.albumState)}`}
                    >
                      {t(`product.${album?.albumState}`)}
                    </div>
                  ) : (
                    <>
                      <div className={`badge state-badge badge-alert`}>
                        {t(`product.changeRequest`)}
                      </div>
                      {changeRequest?.state ? (
                        <div
                          className={`badge state-badge ${getAlbumStateBadgeClass(changeRequest.state)}`}
                        >
                          {t(`product.${changeRequest.state}`)}
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                {productViewMode !== ProductViewModes.ChangeRequestReadOnly && (
                  <div className="editProductView-stateChangeOperations">
                    <StateChangeOperations
                      album={album}
                      setErrorBanner={setErrorBanner}
                      setErrors={setErrors}
                      setApiError={setApiError}
                      submissionsGridUrl={
                        productViewMode === ProductViewModes.ChangeRequest
                          ? releasesGridUrl
                          : submissionsGridUrl
                      }
                      formatType={FormatType.Physical}
                      changeRequest={changeRequest}
                      hasChangeRequest={!!currentChanges?.totalChangesCounter}
                    ></StateChangeOperations>
                  </div>
                )}
                {productViewMode !== ProductViewModes.ChangeRequestReadOnly && (
                  <div className="editProductView-actiondDropdownMenu">
                    <ActionDropdownMenu
                      album={album}
                      setErrorBanner={setErrorBanner}
                      setErrors={setErrors}
                      project={project ?? undefined}
                      onProjectAdded={refetchProject}
                      onProjectRemoved={() =>
                        setShowRemoveFromProjectConfirmationModal(true)
                      }
                      formatType={FormatType.Physical}
                      productViewMode={productViewMode}
                      changeRequest={changeRequest}
                      hasChangeRequest={!!currentChanges?.totalChangesCounter}
                      readonly={readonly}
                    ></ActionDropdownMenu>
                  </div>
                )}
              </div>
              <div className="view-spacer-container"></div>
              <div className="editProductView-enableScroll view-content-container">
                <ContextErrorAlert />
                <DismissibleErrorAlert
                  error={apiError}
                  onClose={() => setApiError(null)}
                />
                {errorBanner ? (
                  <ValidationErrors
                    errorBanner={errorBanner}
                    errors={errorMessages}
                    warningMessages={warningMessages}
                    validationErrors={errors}
                    catalogUpc={album.gtin}
                    region={'View'}
                  ></ValidationErrors>
                ) : null}
                {album.activeChangeRequestId ? (
                  <ErrorAlert
                    titleText={t('product.changeRequest.pendingChangeRequest')}
                    dismissable={false}
                    variant={'warning'}
                    contentText={
                      <span>
                        {t('product.changeRequest.pendingChangeRequestInfo')}
                        <Link to={getActiveChangeRequestUrl(album?.id, false)}>
                          {` ${t('common.generic.clickHere')}`}
                        </Link>
                      </span>
                    }
                  />
                ) : null}
                <div className="editProductView-validationErrorBanner">
                  <RejectionBanner
                    albumState={currentStateHistory}
                  ></RejectionBanner>
                  <div className="product-info">
                    <div className="product-thumbnail">
                      <Uploader
                        albumId={album.id}
                        asset={album.coverArt}
                        assetType={AssetType.CoverArt}
                        isAlbumArt={true}
                        readonly={readonly || !!changeRequest}
                        updateProductAsset={updateProductCoverArt}
                      />
                    </div>
                    <ProductMetadata
                      errors={errors}
                      errorFlag={errorFlag}
                      album={album}
                      project={project ?? undefined}
                      onToggleLabelInfoModal={toggleLabelInfoModal}
                      onToggleParentLabelInfoModal={toggleParentLabelInfoModal}
                      readonly={readonly}
                      onEditProductClicked={toggleEditProductModal}
                      DetailsTableComponent={DetailsTable}
                      changeRequest={changeRequest}
                      currentChanges={currentChanges?.productMetadataChanges}
                    />
                  </div>
                </div>
                <div className="editProductView-spacer-container"></div>
                <Tabs
                  isValidFormat={!!album.format}
                  usesSides={
                    album.format != null && usesSides(album.format?.name)
                  }
                  tracks={album.tracks}
                  reloadTracks={ReloadTracks}
                  restrictions={restrictions}
                  toggleTrackPopUp={toggleEditTrackModal}
                  togglePricingModal={togglePricingModal}
                  toggleMultipleTracksPopUp={toggleEditMultipleTracksModal}
                  toggleRestrictionsModal={toggleRestrictionsModal}
                  album={album}
                  readonly={readonly}
                  errors={errors}
                  stateHistory={album.albumMetaData?.stateHistory}
                  updateProductTracks={UpdateProductTracks}
                  trackSelectionResetSignal={trackSelectionResetSignal}
                  productViewMode={productViewMode}
                  currentChanges={currentChanges}
                  changeRequest={changeRequest}
                ></Tabs>
              </div>
            </div>
            <div>
              {showEditProductModal ? (
                <EditMode
                  togglePopUp={toggleEditProductModal}
                  albumObj={album}
                  errorFlag={errorFlag}
                  errors={errors}
                  readonly={readonly}
                  disabled={disabled}
                  onProductUpdated={onProductUpdated}
                  showValidationErrors={shouldShowValidationErrors}
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.productMetadataChanges}
                ></EditMode>
              ) : null}
              {showLabelInfoModal && labelInfo ? (
                <LabelInfoModal
                  togglePopUp={toggleLabelInfoModal}
                  label={labelInfo}
                ></LabelInfoModal>
              ) : null}
              {showParentLabelInfoModal && parentLabelInfo ? (
                <LabelInfoModal
                  togglePopUp={toggleParentLabelInfoModal}
                  label={parentLabelInfo}
                  isParentLabel
                ></LabelInfoModal>
              ) : null}
              {showEditTrackModal ? (
                <TrackEditView
                  togglePopUp={toggleEditTrackModal}
                  trackObj={track}
                  reloadTracks={() => ReloadTracks()}
                  errorFlag={errorFlag}
                  errors={errors}
                  readonly={readonly}
                  disabled={disabled}
                  catalogUpc={album.gtin}
                  loadPreviousTrack={loadPreviousTrack}
                  loadNextTrack={loadNextTrack}
                  usesSides={
                    album.format != null && usesSides(album.format?.name)
                  }
                  album={album}
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.tracksChanges}
                ></TrackEditView>
              ) : null}
              {showEditMultipleTracksModal ? (
                <TrackEditView
                  togglePopUp={toggleEditMultipleTracksModal}
                  trackObj={track}
                  disabled={disabled}
                  reloadTracks={() => ReloadTracks()}
                  multipleTracksEdit={multipleTracksEdit}
                  onSaveSuccess={handleMultipleTrackEditSaved}
                  changeRequest={changeRequest}
                ></TrackEditView>
              ) : null}
              {showRestrictionsModal && restrictions ? (
                <RestrictionsModal
                  toggleRestrictionsModal={toggleRestrictionsModal}
                  territoryRestrictionSet={restrictions}
                  readonly={readonly}
                  updateProduct={(
                    restrictions: string[],
                    restrictionScheme: RestrictionSchemeType
                  ) =>
                    updateProductRestrictions(restrictions, restrictionScheme)
                  }
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.territoriesChanges}
                ></RestrictionsModal>
              ) : null}
              {showPricingModal ? (
                <PricingModal
                  togglePricingModal={togglePricingModal}
                  album={album}
                  readonly={readonly}
                  updateProductPricing={updateProductPricing}
                  changeRequest={changeRequest}
                  currentChanges={currentChanges?.pricingChanges}
                ></PricingModal>
              ) : null}
            </div>
          </>
        ) : (
          <div className={'spinner-container'}>
            <Spinner size={SpinnerSize.FullPage} />
          </div>
        )}
      </div>
    </div>
  );
};
export default ProductViewTemplate;
